import React from 'react';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { pathByRouteName } from '../../util/routes';
import { NamedLink } from '../../components';

import css from './BottomNav.module.css';

// Boutique thin-line icons (stroke inherits currentColor from the tab).
const IconHome = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 10.5 12 4l8 6.5M6 9.5V19a1 1 0 0 0 1 1h3.5v-5h3v5H17a1 1 0 0 0 1-1V9.5"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const IconHeart = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 20.5s-7.5-4.7-7.5-10A4.2 4.2 0 0 1 12 7.5a4.2 4.2 0 0 1 7.5 3c0 5.3-7.5 10-7.5 10Z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const IconPlus = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 5.5v13M5.5 12h13" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);
const IconBell = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6.5 10a5.5 5.5 0 0 1 11 0c0 4 1.5 5.5 1.5 5.5H5S6.5 14 6.5 10ZM10 18.5a2 2 0 0 0 4 0"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const IconBag = () => (
  // Dawn-stil taske: firkantet med afrundede hjørner + hank på toppen
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <g stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 8.25h12v10.25a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V8.25Z" />
      <path d="M9.25 8.25V7a2.75 2.75 0 0 1 5.5 0v1.25" />
    </g>
  </svg>
);

// Routes where a focused flow / an existing fixed bottom bar takes over,
// so the tab bar should step aside (listing detail has the Buy-now bar,
// listing wizard + checkout are focused flows).
const HIDE_ON_PATH_FRAGMENTS = ['/l/', '/checkout'];

/**
 * Mobile-only, app-style fixed bottom navigation (Vestiaire-style).
 * Additive: desktop keeps the existing Topbar (this is hidden at viewportLarge).
 */
const BottomNav = props => {
  const intl = useIntl();
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();
  const { notificationCount = 0 } = props;

  const currentPath = location.pathname;
  if (HIDE_ON_PATH_FRAGMENTS.some(frag => currentPath.includes(frag))) {
    return null;
  }

  const pathFor = (name, params) => {
    try {
      return pathByRouteName(name, routeConfiguration, params);
    } catch (e) {
      return null;
    }
  };

  const isActive = tabPath => {
    if (!tabPath) return false;
    if (tabPath === '/') return currentPath === '/';
    return currentPath === tabPath || currentPath.startsWith(tabPath + '/');
  };

  // Rækkefølge: opdag → shop → sælg (fremhævet i midten) → gemte → beskeder.
  // "Me" bor i topbaren (konto-ikon).
  const tabs = [
    { key: 'home', label: 'Home', name: 'LandingPage', Icon: IconHome },
    { key: 'buy', label: 'Buy', name: 'SearchPage', Icon: IconBag },
    { key: 'sell', label: 'Sell now', name: 'NewListingPage', Icon: IconPlus, primary: true },
    { key: 'favorites', label: 'Favorites', name: 'FavoritesPage', Icon: IconHeart },
    {
      key: 'notifications',
      label: 'Notifications',
      name: 'InboxPage',
      params: { tab: 'orders' },
      Icon: IconBell,
      badge: notificationCount > 0,
    },
  ];

  return (
    <nav className={css.root} aria-label="Primary">
      <ul className={css.list}>
        {tabs.map(tab => {
          const tabPath = pathFor(tab.name, tab.params);
          const active = isActive(tabPath);
          const { Icon } = tab;
          return (
            <li key={tab.key} className={css.item}>
              <NamedLink
                name={tab.name}
                params={tab.params}
                className={classNames(css.link, {
                  [css.linkActive]: active,
                  [css.linkPrimary]: tab.primary,
                })}
                aria-current={active ? 'page' : undefined}
              >
                <span className={classNames(css.iconWrap, { [css.iconWrapPrimary]: tab.primary })}>
                  <Icon />
                  {tab.badge ? <span className={css.badge} aria-hidden="true" /> : null}
                </span>
                <span className={css.label}>{tab.label}</span>
              </NamedLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default BottomNav;
